import React, { Component, createRef } from 'react';
import PropTypes from 'prop-types';
import { noop } from 'lodash';
import cx from 'classnames';
import { error } from 'utils/error';
import { generateId } from 'utils/string';
import { cloneChildren, omitProps, isElementOfComponent } from 'utils/react';
import { createEnum } from 'utils/enum';
import { CheckboxInput, ManagedRadioInput } from 'components/Input';
import { FormField } from 'components/Form';
import { ProductInfo } from 'components/ProductInfo';
import { RenderProp } from 'components/Utilities/RenderProp';
import { VisuallyHidden } from 'components/Utilities/VisuallyHidden';
import SelectableContext from './SelectableContext';
import styles from './SelectableItem.module.scss';

export const INPUT_TYPE = createEnum('checkbox', 'radio');
export const VERTICAL_ALIGN = createEnum('top', 'middle');

export default class SelectableItem extends Component {
    static displayName = 'SelectableItem';

    static propTypes = {
        innerRef: PropTypes.ref(),
        id: PropTypes.string,
        name: PropTypes.string,
        value: PropTypes.string.isRequired,
        label: PropTypes.node,
        readOnly: PropTypes.bool,
        disabled: PropTypes.bool,
        indeterminate: PropTypes.bool,
        onChange: PropTypes.func,
        onClick: PropTypes.func,
        component: PropTypes.elementType,
        renderDecorator: PropTypes.func,
        className: PropTypes.string,
        inputClassName: PropTypes.string,
        children: PropTypes.node,
        onBeforeChange: PropTypes.func,
    };

    static contextType = SelectableContext;

    static defaultProps = {
        innerRef: noop,
        id: '',
        name: '',
        label: '',
        readOnly: false,
        disabled: false,
        indeterminate: false,
        onChange: noop,
        onClick: noop,
        component: 'div',
        renderDecorator: noop,
        className: null,
        inputClassName: null,
        children: null,
        onBeforeChange: noop,
    };

    state = {
        id: null,
        inputId: generateId('selectable-item-input'),
    };

    inputRef = createRef();

    componentDidMount() {
        const { inputType } = this.context;

        error(!inputType, 'SelectableItem can only be used inside Selectable');
    }

    isSelected() {
        const { value } = this.props;
        const { isItemSelected } = this.context;
        return isItemSelected ? isItemSelected(value) : false;
    }

    isRadio() {
        const { inputType } = this.context;
        return inputType === INPUT_TYPE.RADIO;
    }

    isStandalone() {
        const { component } = this.props;
        return !component;
    }

    handleClick = (event) => {
        const { value, disabled, readOnly, onClick, onChange, onBeforeChange } = this.props;
        const { selectOnClick, disableUncheckedItems, propagateClicksOnInput, toggleItem } =
            this.context;

        const checked = this.isSelected();
        const isInputClick = event.target === this.inputRef.current;

        if (disabled || readOnly) return;
        if (disableUncheckedItems && checked && !isInputClick) return;
        if (this.isRadio() && checked) return;

        if (!isInputClick || propagateClicksOnInput) {
            onClick(event, value);
        }

        if (isInputClick || selectOnClick) {
            if (onBeforeChange(event, value) === false) return;

            toggleItem(value);
            onChange(event, value, !checked);
        }
    };

    handleBeforeChange = (event) => {
        if (this.isStandalone()) {
            this.handleClick(event);
        }

        return false;
    };

    renderInput() {
        const { name, value, label, readOnly, disabled, indeterminate, inputClassName } =
            this.props;

        const { inputId } = this.state;

        const isRadio = this.isRadio();
        const isStandalone = this.isStandalone();
        const checked = this.isSelected();
        const InputComponent = isRadio ? ManagedRadioInput : CheckboxInput;
        const classNames = cx(styles.input, inputClassName);

        return (
            <InputComponent
                innerRef={this.inputRef}
                id={inputId}
                name={name}
                value={value}
                label={label && <VisuallyHidden>{label}</VisuallyHidden>}
                checked={checked}
                readOnly={readOnly}
                disabled={disabled}
                indeterminate={isRadio ? undefined : indeterminate}
                className={classNames}
                onBeforeChange={this.handleBeforeChange}
                onClick={(event) => {
                    if (isStandalone) event.stopPropagation();
                }}
            />
        );
    }

    renderContent() {
        const { children } = this.props;
        const { disableUncheckedItems } = this.context;

        const checked = this.isSelected();

        return (
            <div className={styles.container}>
                {disableUncheckedItems ? cloneChildren(children, { disabled: !checked }) : children}
            </div>
        );
    }

    render() {
        const {
            innerRef,
            value,
            disabled,
            className,
            component: ContainerComponent,
            readOnly,
            renderDecorator,
            children,
            ...rest
        } = this.props;
        const { inputType, verticalAlign, selectOnClick, disableUncheckedItems } = this.context;
        const { id } = this.state;

        if (!inputType) {
            return null;
        }

        if (this.isStandalone()) {
            return this.renderInput();
        }

        const checked = this.isSelected();
        const hasProduct = isElementOfComponent(children, ProductInfo);
        const hasForm = isElementOfComponent(children, FormField, {
            onlySingleChildren: false,
        });
        const isActive = !disabled && !readOnly && (!checked || !disableUncheckedItems);
        const isClickable = isActive && selectOnClick;
        const alignTop = verticalAlign === VERTICAL_ALIGN.TOP;

        const classNames = cx(
            styles.item,
            isClickable && styles.clickable,
            disabled && styles.disabled,
            hasProduct && styles.hasProduct,
            hasForm && styles.hasForm,
            alignTop && styles.top,
            className,
        );

        return (
            <ContainerComponent
                ref={innerRef}
                id={id}
                className={classNames}
                disabled={disabled}
                {...omitProps(rest, SelectableItem, ['isMenuItem'])}
                onClick={this.handleClick}
            >
                <RenderProp render={renderDecorator} argument={{ value }} />
                {this.renderInput()}
                {this.renderContent()}
            </ContainerComponent>
        );
    }
}
